import { useState } from "react";
import { ChevronDown, ChevronRight, ListChecks } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  Coverage,
  Resolutions,
  ResolvedSecurity,
  ResolvedRequest,
  ResolvedSignal,
  DateAdjustment,
  OmittedDate,
} from "@/lib/api/assistantV2";
import { formatCell, humanizeKey } from "../utils";

/** A compact row of `key: value` chips for one resolved entity. */
function KvRow({ obj }: { obj: Record<string, unknown> }) {
  const entries = Object.entries(obj).filter(
    ([, v]) => v != null && !(Array.isArray(v) && v.length === 0),
  );
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-2 text-xs">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-baseline gap-1">
          <span className="text-slate-400">{humanizeKey(k)}:</span>
          <span className="text-slate-800 font-mono">
            {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </span>
        </div>
      ))}
    </div>
  );
}

function SubSection<T extends Record<string, unknown>>({
  title,
  items,
}: {
  title: string;
  items: T[] | undefined;
}) {
  if (items == null || items.length === 0) return null;
  return (
    <div className="border-t border-slate-100">
      <div className="px-4 pt-2 text-[11px] uppercase tracking-wide text-slate-400">
        {title} ({items.length})
      </div>
      <div className="divide-y divide-slate-50">
        {items.map((item, i) => (
          <KvRow key={i} obj={item} />
        ))}
      </div>
    </div>
  );
}

/** The effective data window, with each item's meaning shown on hover. */
function CoverageSection({ coverage }: { coverage: Coverage }) {
  const descriptions = coverage.descriptions ?? {};
  // The numeric/data items, in display order; date_span is a [min, max] tuple.
  const items: { key: keyof Coverage; label: string; value: string }[] = [];
  if (coverage.frames != null)
    items.push({ key: "frames", label: "Frames", value: String(coverage.frames) });
  if (coverage.total_rows != null)
    items.push({
      key: "total_rows",
      label: "Total rows",
      value: String(coverage.total_rows),
    });
  if (coverage.date_span)
    items.push({
      key: "date_span",
      label: "Date span",
      value: `${formatCell(coverage.date_span[0])} – ${formatCell(coverage.date_span[1])}`,
    });

  return (
    <div className="border-t border-slate-100">
      <div className="px-4 pt-2 text-[11px] uppercase tracking-wide text-slate-400">
        Coverage
      </div>
      <TooltipProvider delayDuration={150}>
        <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-2 text-xs">
          {items.map(({ key, label, value }) => {
            const desc = descriptions[key as keyof typeof descriptions];
            return (
              <div key={key} className="flex items-baseline gap-1">
                {desc ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-slate-400 cursor-help underline decoration-dotted decoration-slate-300 underline-offset-2">
                        {label}:
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{desc}</TooltipContent>
                  </Tooltip>
                ) : (
                  <span className="text-slate-400">{label}:</span>
                )}
                <span className="text-slate-800 font-mono">{value}</span>
              </div>
            );
          })}
        </div>
      </TooltipProvider>
    </div>
  );
}

export function ResolutionsPanel({
  resolutions,
}: {
  resolutions: Resolutions;
}) {
  const [open, setOpen] = useState(false);

  const securities = resolutions.securities as ResolvedSecurity[] | undefined;
  const requests = resolutions.requests as ResolvedRequest[] | undefined;
  const signals = resolutions.signals as ResolvedSignal[] | undefined;
  const dateAdjustments = resolutions.date_adjustments as
    | DateAdjustment[]
    | undefined;
  const omittedDates = resolutions.omitted_dates as OmittedDate[] | undefined;
  const coverage = resolutions.coverage;

  const count =
    (securities?.length ?? 0) +
    (requests?.length ?? 0) +
    (signals?.length ?? 0) +
    (dateAdjustments?.length ?? 0) +
    (omittedDates?.length ?? 0) +
    (coverage ? 1 : 0);

  if (count === 0) return null;

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-slate-50"
      >
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <ListChecks className="h-4 w-4 text-slate-500" />
          Show the work
          <span className="text-xs text-slate-500">({count})</span>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-slate-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-slate-500" />
        )}
      </button>
      {open && (
        <div className="pb-2">
          <SubSection title="Securities" items={securities} />
          <SubSection title="Requests" items={requests} />
          <SubSection title="Signals" items={signals} />
          <SubSection title="Date adjustments" items={dateAdjustments} />
          <SubSection title="Omitted dates" items={omittedDates} />
          {coverage && <CoverageSection coverage={coverage} />}
        </div>
      )}
    </div>
  );
}
